import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';

interface Fila { campo: string; valor: string; vacio: boolean; }

/**
 * Datos técnicos del módulo Odoo en una Card: lo enviado o lo respondido como filas «campo → valor»,
 * con «Ver JSON» para el bloque crudo y «Copiar».
 * <od-kv titulo="Lo que se envió" [datos]="t.enviado" vacio="Sin datos."></od-kv>
 * Los objetos anidados se aplanan con punto (address.city); las listas de valores simples se unen con coma.
 */
@Component({
  selector: 'od-kv',
  template: `
    <section class="od-cd">
      <div class="od-cd-h">
        <h5>{{ titulo }}</h5>
        <ng-container *ngIf="hay">
          <button type="button" class="od-btn od-btn-sm od-btn-ghost" (click)="json = !json" [attr.aria-pressed]="json">
            <od-icon [name]="json ? 'list' : 'file'"></od-icon>{{ json ? 'Ver campos' : 'Ver JSON' }}
          </button>
          <button type="button" class="od-btn od-btn-sm od-btn-ghost" (click)="copiar()" aria-label="Copiar JSON">
            <od-icon [name]="copiado ? 'check' : 'copy'"></od-icon>{{ copiado ? 'Copiado' : 'Copiar' }}
          </button>
        </ng-container>
      </div>
      <p class="od-cd-b od-muted" *ngIf="!hay">{{ vacio }}</p>
      <dl class="od-rows od-rows-kv" *ngIf="hay && !json" style="border-top:1px solid var(--od-line)">
        <div *ngFor="let f of filas; trackBy: porCampo">
          <dt class="od-mono">{{ f.campo }}</dt>
          <dd [class.od-muted]="f.vacio">{{ f.valor }}</dd>
        </div>
      </dl>
      <div class="od-cd-b" *ngIf="hay && json"><pre class="od-code">{{ texto }}</pre></div>
    </section>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdKvComponent {
  @Input() titulo = '';
  @Input() vacio = 'Sin respuesta.';
  filas: Fila[] = [];
  texto = '';
  hay = false;
  json = false;
  copiado = false;

  @Input() set datos(d: unknown) {
    this.hay = d !== null && d !== undefined && d !== '' && !(typeof d === 'object' && !Object.keys(d as object).length);
    this.texto = typeof d === 'string' ? d : JSON.stringify(d, null, 2);
    this.filas = [];
    if (this.hay && typeof d === 'object') this.aplanar(d as Record<string, unknown>, '');
    else if (this.hay) this.filas.push({ campo: 'valor', valor: String(d), vacio: false });
  }

  constructor(private cd: ChangeDetectorRef) {}

  private aplanar(o: Record<string, unknown>, prefijo: string): void {
    for (const [k, v] of Object.entries(o)) {
      const campo = prefijo ? `${prefijo}.${k}` : k;
      const simple = (x: unknown) => x === null || typeof x !== 'object';
      if (v && typeof v === 'object' && !Array.isArray(v)) this.aplanar(v as Record<string, unknown>, campo);
      else if (Array.isArray(v) && !v.every(simple)) v.forEach((x, i) => simple(x) ? this.filas.push(this.fila(`${campo}[${i}]`, x)) : this.aplanar(x as Record<string, unknown>, `${campo}[${i}]`));
      else this.filas.push(this.fila(campo, Array.isArray(v) ? v.join(', ') : v));
    }
  }

  private fila(campo: string, v: unknown): Fila {
    const vacio = v === null || v === undefined || v === '';
    return { campo, valor: vacio ? '—' : typeof v === 'boolean' ? (v ? 'sí' : 'no') : String(v), vacio };
  }

  copiar(): void {
    navigator.clipboard?.writeText(this.texto).then(() => {
      this.copiado = true; this.cd.markForCheck();
      setTimeout(() => { this.copiado = false; this.cd.markForCheck(); }, 1500);
    });
  }

  porCampo = (_: number, f: Fila): string => f.campo;
}
